import { useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Plus } from 'lucide-react';
import { TASK_PRIORITIES, TASK_STATUSES } from '@mkcore/projects/schema';
import {
  Button,
  Checkbox,
  EmptyState,
  ErrorState,
  Field,
  Input,
  NativeSelect,
  PageHeader,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@mkcore/ui';
import { useErrorMessage } from '@/lib/mutation';
import { useCan } from '@/lib/session';
import { TaskBoard, TaskDialog, TaskFilters, TaskList, taskPagesQuery } from '@/features/projects/tasks';

const search = z.object({
  // The router parses search values as JSON: a search for 12 arrives as a number.
  q: z
    .union([z.string(), z.number()])
    .optional()
    .transform((v) => (v === undefined ? undefined : String(v))),
  mine: z.boolean().optional(),
  overdue: z.boolean().optional(),
  // Who the task lies with (#327): true only client tasks, false only internal ones.
  clientTask: z.boolean().optional(),
  status: z.enum(TASK_STATUSES).optional(),
  priority: z.enum(TASK_PRIORITIES).optional(),
  view: z.enum(['list', 'board']).optional(),
  // On a project (with its subprojects), an engagement or a customer (#292).
  projectId: z.string().optional(),
  engagementId: z.string().optional(),
  organisationId: z.string().optional(),
});
type Search = z.infer<typeof search>;

export const Route = createFileRoute('/_app/tasks/')({ validateSearch: search, component: TasksPage });

/**
 * All tasks with filters (CO-0445), on projects and without one on an engagement, organisation or lead, as a list or a
 * board by status (CO-0435). Under the list the planned CRM activities, so follow-ups are in one place (#301).
 */
function TasksPage() {
  const { t } = useTranslation();
  const s = Route.useSearch();
  const message = useErrorMessage();
  const canCreate = useCan('projects:create');
  const [adding, setAdding] = useState(false);
  const navigate = useNavigate({ from: Route.fullPath });
  const set = (patch: Partial<Search>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true });
  const board = s.view === 'board';
  // The board shows done tasks too; the list the chosen status, open ones by default.
  const status = board ? (['todo', 'in_progress', 'done'] as const) : s.status ? [s.status] : undefined;
  const q = useInfiniteQuery(
    taskPagesQuery({
      q: s.q || undefined,
      mine: s.mine || undefined,
      overdue: s.overdue || undefined,
      clientTask: s.clientTask,
      status: status ? [...status] : undefined,
      priority: s.priority ? [s.priority] : undefined,
      projectId: s.projectId,
      engagementId: s.engagementId,
      organisationId: s.organisationId,
      sort: 'due',
    }),
  );
  const items = useMemo(() => q.data?.pages.flatMap((p) => p.items) ?? [], [q.data]);

  return (
    <>
      <PageHeader
        eyebrow={t('nav.projects')}
        title={t('tasks.title')}
        description={t('tasks.description')}
        actions={
          canCreate && (
            <Button onClick={() => setAdding(true)}>
              <Plus /> {t('tasks.new')}
            </Button>
          )
        }
      />
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label={t('common.search')} className="w-64">
          {(p) => (
            <Input
              {...p}
              placeholder={t('tasks.searchHint')}
              value={s.q ?? ''}
              onChange={(e) => set({ q: e.target.value || undefined })}
            />
          )}
        </Field>
        {!board && (
          <Field label={t('tasks.fields.status')} className="w-44">
            {(p) => (
              <NativeSelect
                {...p}
                value={s.status ?? ''}
                onChange={(e) => set({ status: (e.target.value || undefined) as never })}
              >
                <option value="">{t('tasks.open')}</option>
                {TASK_STATUSES.map((st) => (
                  <option key={st} value={st}>
                    {t(`tasks.status.${st}`)}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
        )}
        <Field label={t('tasks.fields.priority')} className="w-40">
          {(p) => (
            <NativeSelect
              {...p}
              value={s.priority ?? ''}
              onChange={(e) => set({ priority: (e.target.value || undefined) as never })}
            >
              <option value="">{t('common.all')}</option>
              {TASK_PRIORITIES.map((pr) => (
                <option key={pr} value={pr}>
                  {t(`tasks.priority.${pr}`)}
                </option>
              ))}
            </NativeSelect>
          )}
        </Field>
        <Field label={t('tasks.liesWith')} className="w-44">
          {(p) => (
            <NativeSelect
              {...p}
              value={s.clientTask === undefined ? '' : String(s.clientTask)}
              onChange={(e) =>
                set({ clientTask: e.target.value === '' ? undefined : e.target.value === 'true' })
              }
            >
              <option value="">{t('common.all')}</option>
              <option value="false">{t('tasks.internalOnly')}</option>
              <option value="true">{t('tasks.clientOnly')}</option>
            </NativeSelect>
          )}
        </Field>
        <TaskFilters value={s} onChange={set} />
        <label className="flex h-9 items-center gap-2 text-sm">
          <Checkbox checked={!!s.mine} onCheckedChange={(v) => set({ mine: v === true || undefined })} />{' '}
          {t('tasks.mine')}
        </label>
        <label className="flex h-9 items-center gap-2 text-sm">
          <Checkbox
            checked={!!s.overdue}
            onCheckedChange={(v) => set({ overdue: v === true || undefined })}
          />{' '}
          {t('tasks.overdueOnly')}
        </label>
        <Tabs
          value={board ? 'board' : 'list'}
          onValueChange={(v) => set({ view: v === 'board' ? 'board' : undefined })}
        >
          <TabsList>
            <TabsTrigger value="list">{t('tasks.list')}</TabsTrigger>
            <TabsTrigger value="board">{t('tasks.board')}</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      {q.isError ? (
        <ErrorState title={t('errors.generic')} description={message(q.error)} />
      ) : !q.data ? (
        <Skeleton className="h-64" />
      ) : !items.length ? (
        <EmptyState title={t('tasks.none')} description={t('tasks.emptyHint')} />
      ) : board ? (
        <TaskBoard items={items} />
      ) : (
        <TaskList
          items={items}
          partial={q.hasNextPage}
          onNeedAll={() => q.hasNextPage && !q.isFetchingNextPage && void q.fetchNextPage()}
        />
      )}
      {q.hasNextPage && (
        <div className="mt-3 flex justify-center">
          <Button variant="secondary" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {t('common.more')}
          </Button>
        </div>
      )}
      {adding && <TaskDialog onClose={() => setAdding(false)} />}
    </>
  );
}
